import * as Highcharts from 'highcharts';
import { SeriesPieElementCustomData } from '../types/chartData';
import { BasePieChartProps } from '../types/chartProps';
import { formatValue } from './formatters';
import { applyColors } from './colorsUtils';
import {
  calculateLegendYOffset,
  createLegendClickHandler,
  getLegendLabel,
  normalizeAlign,
  normalizeVerticalAlign
} from './legendUtils';
import { createSeriesElementClickHandler } from './seriesUtils';
import { applyDescriptionOptions, applyTitleOptions } from './commonChartUtils';
import { escapeHtml } from '@opengov/reporting-types/src/escapeHtml';
import { dataLine, header } from './tooltipUtils';

function getDataLabelPrimaryValue(this: Highcharts.PointLabelObject, props: BasePieChartProps) {
  return props.isPercentChart
    ? formatValue(props.formatting, this.y ?? 0, 'percentage')
    : formatValue(props.formatting, this.y ?? 0, props.valueDisplayType);
}

function getDataLabelSecondaryValue(this: Highcharts.PointLabelObject, props: BasePieChartProps) {
  const custom = this.point.options.custom ?? {};
  const amount = custom?.amount ?? 0;
  const percent = custom?.percentTotal ?? this.percentage / 100;
  return props.isPercentChart
    ? formatValue(props.formatting, amount, props.valueDisplayType)
    : formatValue(props.formatting, percent, 'percentage');
}

function getSeriesTotal(context: Highcharts.Chart) {
  const series = context.series?.[0];
  if (series && 'total' in series) {
    return (series as { total: number }).total;
  }
}

function getFormattedLabelSubtitle(props: BasePieChartProps, total?: number) {
  const { subTitle } = props.label;

  if (subTitle) {
    if (typeof subTitle === 'number') {
      return formatValue(props.formatting, subTitle, props.valueDisplayType);
    }
    return subTitle;
  }

  if (total) {
    return formatValue(props.formatting, total, props.valueDisplayType);
  }

  return '';
}

function prepareSeriesDataForPieChart(params: BasePieChartProps): Highcharts.SeriesPieOptions[] {
  return params.series.map((el) => {
    const series: Highcharts.SeriesPieOptions = {
      type: 'pie',
      name: el.name,
      data: el.data.map((d, index) => {
        const { name, value, visible, ...custom } = d;
        return {
          name: name,
          y: value,
          visible: visible ?? true,
          custom,
          ...(params.colors ? { color: params.colors[index] } : {})
        };
      }),
      ...(params.innerSize ? { innerSize: params.innerSize } : {})
    };

    return series;
  });
}

export function renderTooltipForPieChart(props: BasePieChartProps) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const custom = this.point.options.custom ?? {};
    const category = custom.displayName ?? this.key;

    const displayValue = props.isPercentChart
      ? formatValue(props.formatting, this.y ?? 0, 'percentage')
      : formatValue(props.formatting, this.y ?? 0, props.valueDisplayType);

    const customObjWithKeysAndValues: Record<keyof SeriesPieElementCustomData, string> = {
      amount: 'Amount',
      total: 'Total',
      percentTotal: 'Percent of Total'
    };

    const customKeysArray: (keyof SeriesPieElementCustomData)[] = ['amount', 'total', 'percentTotal'];

    let lowerTooltip = ``;
    for (const key of customKeysArray) {
      if (custom[key]) {
        let percentValue = '';
        let formattedValue = '';
        switch (key) {
          case 'percentTotal':
            percentValue = formatValue(props.formatting, custom[key], 'percentage');
            lowerTooltip += dataLine(customObjWithKeysAndValues[key], percentValue);
            break;
          default:
            formattedValue = formatValue(props.formatting, custom[key], props.valueDisplayType);
            lowerTooltip += dataLine(customObjWithKeysAndValues[key], formattedValue);
        }
      }
    }

    lowerTooltip = lowerTooltip ? `<br/>${lowerTooltip}` : '';

    return header(this.series.name, category, displayValue) + lowerTooltip;
  };
}

export function renderLabelsForPieChart(props: BasePieChartProps) {
  return function (
    this: Highcharts.Chart & {
      customLabelTitle?: Highcharts.SVGElement;
      customLabelSubtitle?: Highcharts.SVGElement;
    }
  ) {
    this.customLabelTitle?.destroy();
    this.customLabelSubtitle?.destroy();

    const total = getSeriesTotal(this);

    const size = {
      title: 18,
      subtitle: 16
    };
    const pos = {
      title: {
        x: this.plotLeft + this.plotWidth / 2,
        y: this.plotTop + this.plotHeight + 50
      },
      subtitle: {
        x: this.plotLeft + this.plotWidth / 2,
        y: this.plotTop + this.plotHeight + 70
      }
    };

    this.customLabelTitle = this.renderer
      .text(escapeHtml(props.label.title), pos.title.x, pos.title.y)
      .attr({
        align: 'center'
      })
      .css({
        fontWeight: 'bold',
        fontSize: `${size.title}px`
      })
      .add();

    this.customLabelSubtitle = this.renderer
      .text(escapeHtml(getFormattedLabelSubtitle(props, total)), pos.subtitle.x, pos.subtitle.y)
      .attr({
        align: 'center'
      })
      .css({
        fontSize: `${size.subtitle}px`
      })
      .add();
  };
}

export function renderDataLabelsForPieChart(props: BasePieChartProps) {
  return function (this: Highcharts.PointLabelObject) {
    const custom = this.point.options.custom ?? {};
    const category = escapeHtml(custom.displayName ?? this.key);
    const primaryValue = escapeHtml(getDataLabelPrimaryValue.call(this, props));
    if (props.isPercentChart) {
      return `<div><b>${category}:</b><br />${primaryValue}</div>`;
    }
    const secondaryValue = escapeHtml(getDataLabelSecondaryValue.call(this, props));
    return `<div><b>${category}:</b><br />${primaryValue} (${secondaryValue})</div>`;
  };
}

export function applyPieChartSeriesOptions(options: Highcharts.Options, params: BasePieChartProps) {
  options.series = prepareSeriesDataForPieChart(params);
  return options;
}

export function applyPieChartLegendOptions(options: Highcharts.Options, params: BasePieChartProps) {
  const align = normalizeAlign(params.legend?.align);
  const verticalAlign = normalizeVerticalAlign(params.legend?.verticalAlign);
  const yOffset = calculateLegendYOffset(align, verticalAlign);

  options.legend = {
    ...options.legend,
    enabled: !params.hideLegends,
    align,
    verticalAlign,
    y: yOffset,
    events: {
      ...options.legend?.events,
      itemClick: createLegendClickHandler(params.onLegendClick)
    },
    labelFormatter: getLegendLabel
  };
  return options;
}

export function applyPieChartDataLabels(options: Highcharts.Options, params: BasePieChartProps) {
  options.plotOptions = {
    pie: {
      ...options.plotOptions?.pie,
      dataLabels: {
        enabled: true,
        style: {
          fontWeight: 'normal'
        },
        formatter: renderDataLabelsForPieChart(params)
      }
    }
  };
  return options;
}

export function applyPieChartPlotSeriesOptions(options: Highcharts.Options, params: BasePieChartProps) {
  options.plotOptions = {
    pie: {
      ...options.plotOptions?.pie,
      cursor: params.onSeriesElementClick ? 'pointer' : 'default',
      point: {
        events: {
          click: createSeriesElementClickHandler(params.onSeriesElementClick, 'pie')
        }
      }
    }
  };
  return options;
}

export function applyPieChartTooltipOptions(options: Highcharts.Options, params: BasePieChartProps) {
  options.tooltip = {
    ...options.tooltip,
    formatter: renderTooltipForPieChart(params)
  };
  return options;
}

export function applyPieChartResponsiveSpacing(options: Highcharts.Options) {
  // Add extra spacing at small breakpoints to accommodate both legend and custom labels below pie chart
  if (options.responsive?.rules) {
    options.responsive.rules = options.responsive.rules.map((rule) => ({
      ...rule,
      chartOptions: {
        ...rule.chartOptions,
        chart: {
          ...rule.chartOptions?.chart,
          marginBottom: 160
        }
      }
    }));
  }
  return options;
}

export function applyPieChartOptions(options: Highcharts.Options, params: BasePieChartProps) {
  const fns = [
    applyTitleOptions,
    applyDescriptionOptions,
    applyPieChartSeriesOptions,
    applyPieChartLegendOptions,
    applyPieChartDataLabels,
    applyPieChartPlotSeriesOptions,
    applyPieChartTooltipOptions,
    applyColors,
    applyPieChartResponsiveSpacing
  ];

  return fns.reduce((acc, fn) => fn(acc, params), options);
}
